import { isOfType } from "../utils";

interface ITableTemplate {
  scope: string;
  id: string;
  type: string; // seems to just be "link_by_id" but need to double check
}

interface ITableVariable {
  name: string;
  headers: string[];
  attribute_constraints?: [];
  type_selector?: string;
  template?: ITableTemplate;
  type: string; // dont have clear types outline from backend yet, also not really using this field just yet
  field?: string;
  scope?: string;
}

// possiblities for each field isn't enumerated in backend docs and aren't used in frontend just yet so its okay
interface ITableColumn {
  data_source: string;
  suffix?: string;
  type?: string;
  target_units?: string;
  sort_order?: string;
  format?: string;
}

export interface ITableConfigBase {
  name: string;
  datasets: string[];
  gemd_query?: {
    criteria?: GemdCriterium[];
  };
}

interface Unit {
  unitStr: string;
}

interface IntegerFilter {
  lowerFilter: number;
  upperFilter: number;
  lowerIsInclusive: boolean;
  upperIsInclusive: boolean;
}

interface RealFilter extends IntegerFilter {
  unit: Unit;
}

export type MaterialRunClassification =
  "atomic_ingredient" | "intermediate_ingredient" | "terminal_material";

export interface ClassificationCriterium {
  type: "material_run_classification_criteria";
  classifications: MaterialRunClassification[];
}

export const isClassificationCriterium = isOfType<
  ClassificationCriterium,
  GemdCriterium
>("material_run_classification_criteria");

export interface ConnectivityClassCriteria {
  type: "connectivity_class_criteria";
  is_consumed?: boolean;
  is_produced?: boolean;
}

interface CreatedAtCriteria {
  type: "created_at_criteria";
  // Unix timestamp UTC
  created_from: number;
  created_to: number;
}

export const isConnectivityClassCriterium = isOfType<
  ConnectivityClassCriteria,
  GemdCriterium
>("connectivity_class_criteria");

export const RAW_ISOLATED_CLASS_CRITERIA: ConnectivityClassCriteria = {
  type: "connectivity_class_criteria",
  is_produced: false,
};

export const TERMINAL_CLASS_CRITERIA: ConnectivityClassCriteria = {
  type: "connectivity_class_criteria",
  is_produced: true,
  is_consumed: false,
};

export interface NameCriterium {
  type: "name_criteria";
  name: string;
  search_type: "EXACT" | "SUBSTRING" | "PREFIX" | "SUFFIX";
}

export const isNameCriterium = isOfType<NameCriterium, GemdCriterium>(
  "name_criteria",
);

export interface GemdCategoricalFilter {
  type: "nominal_categorical_filter";
  categories: string[];
}

export interface GemdIntegerFilter {
  type: "nominal_integer_filter" | "uniform_integer_filter";
  values: IntegerFilter;
}

export interface GemdRealFilter {
  type: "nominal_real_filter" | "normal_real_filter" | "uniform_real_filter";
  values: RealFilter;
}

export interface AllIntegerFilter {
  type: "all_integer_filter";
  lower: number;
  upper: number;
  inclusive: boolean;
}

export interface AllRealFilter {
  type: "all_real_filter";
  lower: number;
  upper: number;
  unit: string;
}

export type GemdFilter =
  | GemdCategoricalFilter
  | GemdIntegerFilter
  | AllIntegerFilter
  | GemdRealFilter
  | AllRealFilter;

export interface PropertiesCriterium {
  type: "properties_criteria";
  property_templates_filter: string[];
  value_type_filter?: GemdFilter;
}

export const isPropertiesCriterium = isOfType<
  PropertiesCriterium,
  GemdCriterium
>("properties_criteria");

export interface PropertyAndConditionCriterium {
  type: "property_and_condition_criteria";
  property_template_filter: string;
  property_value_filter?: GemdFilter;
  condition_template_filter: string;
  condition_value_filter?: GemdFilter;
}

export const isPropertyAndConditionCriterium = isOfType<
  PropertyAndConditionCriterium,
  GemdCriterium
>("property_and_condition_criteria");

export interface OrOperator {
  type: "or_operator";
  criteria: GemdCriterium[];
}

export const isOrOperator = isOfType<OrOperator, GemdCriterium>("or_operator");

interface AndOperator {
  type: "and_operator";
  criteria: GemdCriterium[];
}

export const isAndOperator = isOfType<AndOperator, GemdCriterium>(
  "and_operator",
);

export interface TagsCriterium {
  type: "tags_criteria";
  filter_type?:
    "and_tags_filter_type" | "not_tags_filter_type" | "or_tags_filter_type";
  tags: string[];
}

export const isTagsCriterium = isOfType<TagsCriterium, GemdCriterium>(
  "tags_criteria",
);

interface UpdatedAtCriteria {
  type: "updated_at_criteria";
  created_from: number;
  created_to: number;
}

export type GemdCriterium =
  | AndOperator
  | NameCriterium
  | ClassificationCriterium
  | ConnectivityClassCriteria
  | CreatedAtCriteria
  | OrOperator
  | PropertiesCriterium
  | PropertyAndConditionCriterium
  | {
      type: "terminal_material_run_identifiers_criteria";
      terminal_material_ids: {
        scope: string;
        id: string;
      }[];
    }
  | {
      type: "material_template_criteria";
      material_templates_identifiers: {
        scope?: string;
        id: string;
      }[];
      tag_filters: string[];
    }
  | TagsCriterium
  | UpdatedAtCriteria;

export interface ITableConfig extends ITableConfigBase {
  description: string;
  rows: {
    type: "material_run_by_template";
    templates: ITableTemplate[];
  }[];
  variables: ITableVariable[];
  columns: ITableColumn[];
}

export interface ITableGenerateConfigResponse {
  config: ITableConfig;
  ambiguous: unknown[]; // very weird field, not sure how to define it given backend docs but we dont use it so not a problem yet
}

export interface ITableConfigCreatePayload {
  definition: ITableConfig;
}

export interface ITableConfigCreateResponse {
  create_time: number;
  created_by: string;
  description: string;
  id: string;
  name: string;
  update_time: number;
  updated_by: string;
  version: number;
}

export interface ITableBuildRequest {
  tableConfigId: string;
  tableConfigVersion: number;
}
